import { Module } from 'vuex';
import { Modal, ModalType } from '@/components/utils/Modal/Modal.models';
import { Conflict } from '@/utils/api/api';
import { RootState } from '..';
import { Notification } from '@/components/NotificationBanner/NotificationBanner.models';
import { ImageGallerySlide } from '@/components/ImageGallery/ImageGallery.models';
import type { TranslateResult } from 'vue-i18n';

const OPEN_MODAL = 'OPEN_MODAL';
const CLOSE_MODAL = 'CLOSE_MODAL';
const OPEN_NOTIFICATION = 'OPEN_NOTIFICATION';
const CLOSE_NOTIFICATION = 'CLOSE_NOTIFICATION';
const CLOSE_NOTIFICATIONS_OF_ORIGIN = 'CLOSE_NOTIFICATIONS_OF_ORIGIN';
const RESET_STATE = 'RESET_STATE';

export interface NotifyState {
  openModals: Modal[];
  openNotifications: Notification[];
}

export interface InfoPayload {
  headline: string | TranslateResult;
  message: string | TranslateResult;
  acceptCta: string | TranslateResult;
  declineCta: string | TranslateResult;
}

const notifyInitialState = (): NotifyState => ({
  openModals: [],
  openNotifications: []
});

export const notifyModule: Module<NotifyState, RootState> = {
  namespaced: true,
  state: notifyInitialState(),
  getters: {},
  actions: {
    openModal({ commit }, modal: Modal) {
      commit(OPEN_MODAL, modal);
    },
    closeModal({ commit }, modalId: number) {
      commit(CLOSE_MODAL, modalId);
    },
    openConflictModal({ dispatch }, conflicts: Conflict[]) {
      dispatch('openModal', {
        componentId: 'ConflictPrompt',
        type: ModalType.Dialog,
        closeBtn: true,
        props: {
          conflicts
        }
      } as Modal<{
        conflicts: Conflict[];
      }>);
    },
    openCodeModal({ dispatch }) {
      dispatch('openModal', {
        componentId: 'ConfigCodeDialog',
        type: ModalType.Dialog,
        closeBtn: true,
        props: {}
      } as Modal);
    },
    openInfoModal({ dispatch }, infoPayload: InfoPayload) {
      return new Promise<void>((resolve, reject) => {
        dispatch('openModal', {
          componentId: 'InfoDialog',
          type: ModalType.Info,
          closeBtn: false,
          props: { ...infoPayload, resolve, reject }
        } as Modal);
      });
    },
    openGalleryModal({ dispatch }, slides: ImageGallerySlide[]) {
      return new Promise<void>(() => {
        dispatch('openModal', {
          componentId: 'ImageGallery',
          type: ModalType.FullScreen,
          closeBtn: true,
          withoutScrollbar: true,
          props: { slides }
        } as Modal);
      });
    },
    showNotification({ commit, state }, notificationData: Notification) {
      if (!state.openNotifications.some(notification => notification.key === notificationData.key)) {
        commit(OPEN_NOTIFICATION, {
          ...notificationData,
          key: notificationData.key ?? Date.now(),
          closable: notificationData.closable === false ? false : true // check if explicitely set to false, otherwise default to true
        });
      }
    },
    closeNotificationsOfOrigin({ commit }, origin: string) {
      commit(CLOSE_NOTIFICATIONS_OF_ORIGIN, origin);
    },
    closeNotification({ commit }, key: string) {
      commit(CLOSE_NOTIFICATION, key);
    },
    resetState({ commit }) {
      commit(RESET_STATE);
    }
  },
  mutations: {
    [OPEN_MODAL](state, modal: Modal) {
      state.openModals = [
        ...state.openModals,
        {
          id: Date.now(),
          ...modal
        }
      ];
    },
    [CLOSE_MODAL](state, modalId: number) {
      state.openModals = [...state.openModals.filter(modal => modal.id !== modalId)];
    },
    [OPEN_NOTIFICATION](state, notification: Notification) {
      state.openNotifications = [...state.openNotifications, notification];
    },
    [CLOSE_NOTIFICATIONS_OF_ORIGIN](state, origin: string) {
      state.openNotifications = [...state.openNotifications.filter(notification => notification.origin !== origin)];
    },
    [CLOSE_NOTIFICATION](state, key: string) {
      state.openNotifications = [...state.openNotifications.filter(notification => notification.key !== key)];
    },
    [RESET_STATE](state) {
      Object.assign(state, notifyInitialState());
    }
  }
};
